import { AppUtils } from '../../shared/utils/index';
import { BasicViewService } from './BasicViewService';
import { VesDamageKostenbelegModel } from '../models/view/VesDamageKostenbeleg';
import { ZusammenfassungStrukturApi } from "../models/generated/SchadenmeldungZusammenfassungStrukturApi";
import { ParagraphApi } from "../models/generated/StrukturdatenParagraphApi";
import { RowApi } from "../models/generated/StrukturdatenRowApi";
import { FooterAbsatzApi } from "../models/generated/StrukturdatenFooterAbsatzApi";
import { PersonalDataModel } from "../models/view/PersonalData";
import { User } from '../models/api/User/User';

/**
 * ViewService with damage declaration specific extensions.
 */
export class VesDamageViewService extends BasicViewService<VesDamageAppDefinition> {
	protected ownDescriptionTextarea: string;
	protected costType: string;
	protected costTypeSummaryClass: string;
	protected uploadStoredFileData: string;
	protected uploadFileSumClass: string;
	protected uploadFileUploadClass: string;
	protected uploadCostContainerClass: string;
	protected uploadCostType1Id: string;
	protected uploadCostType2Id: string;
	protected uploadCostType3Id: string;
	protected uploadCostType4Id: string;
	protected uploadCostType5Id: string;
	protected damageTypeHeadlineClass: string;
	protected costTypeContainerClass: string;
	protected cloneSource: string;
	protected costSumLabelClass: string;
	protected costSumValueClass: string;
	protected costSumUnitClass: string;
	protected uploadCostTypeActiveUploadsClasses: string;
	protected uploadCostTypeContentContainerClass: string;
	protected lightboxClass: string;
	protected checkPageHeadlineClass: string;
	protected checkPageParagraphClass: string;
	protected checkPageRowClass: string;
	protected checkPageRowHeadlineClass: string;
	protected checkPageRowValueClass: string;
	protected checkPageRowMultipleClass: string;
	protected checkPageRowMultipleValueClass: string;
	protected checkPageFooterTitleClass: string;
	protected checkPageFooterTextClass: string;
	protected successPageSuccessTextClass: string;
	protected readonly abweichenderKontoInhaberName: string;
	protected readonly vornameName: string;
	protected readonly nachnameName: string;
	protected readonly kontoinhaberName: string;
	protected readonly dataGuid: string;
	protected readonly opponentClass: string;
	protected readonly addOpponentButtonClass: string;
	protected altBgSectionSelectors: string[];

	private _emptyLabels = 0;


	constructor(parent: StepController, options: BasicAppOptions<VesDamageAppDefinition>, loader: BasicLoader<VesDamageAppDefinition>) {
		super(parent, options, loader);

		this.ownDescriptionTextarea = '.js-own-description-textarea';
		this.costType = '[name="Kostenart"]';
		this.costTypeSummaryClass = '.js-cost-type-summary';
		this.uploadCostContainerClass = '.js-cost-upload-container';
		this.uploadStoredFileData = 'div[class*="is-active js-file-stored"]';
		this.uploadFileSumClass = '.js-file-sum';
		this.uploadFileUploadClass = '.js-file-upload';

		this.uploadCostType1Id = '#UploadCostTypeTreatment';
		this.uploadCostType2Id = '#UploadCostTypeMedications';
		this.uploadCostType3Id = '#UploadCostTypePatientTransport';
		this.uploadCostType4Id = '#UploadCostTypeMisc';
		this.uploadCostType5Id = '#UploadCostTypeMisc';

		this.damageTypeHeadlineClass = '.schadentyp-headline';
		this.costTypeContainerClass = '.mm-tile-container';
		this.cloneSource = 'js-clone-source';
		this.costSumLabelClass = '.ll-label';
		this.costSumValueClass = '.js-value';
		this.costSumUnitClass = '.js-unit';
		this.uploadCostTypeActiveUploadsClasses = '.js-data.is-active';
		this.uploadCostTypeContentContainerClass = '.m-ves-rs-contentbox';
		this.lightboxClass = '.mfp-hide';
		this.checkPageHeadlineClass = '.js-check-page-headline';
		this.checkPageParagraphClass = '.js-check-page-paragraph';
		this.checkPageRowClass = '.js-check-page-row';
		this.checkPageRowHeadlineClass = '.js-check-page-row-headline';
		this.checkPageRowValueClass = '.js-check-page-row-value';
		this.checkPageRowMultipleClass = '.js-check-page-row-multiple';
		this.checkPageRowMultipleValueClass = '.js-check-page-row-multiple-value';
		this.checkPageFooterTitleClass = '.js-check-page-footer-title';
		this.checkPageFooterTextClass = '.js-check-page-footer-text';
		this.successPageSuccessTextClass = '.js-success-text';

		this.abweichenderKontoInhaberName = 'input[name="AbweichenderKontoInhaber"]';
		this.vornameName = 'input[name="Vorname"]';
		this.nachnameName = 'input[name="Nachname"]';
		this.kontoinhaberName = 'input[name="Kontoinhaber"]';
		this.dataGuid = 'data-js-guid';
		this.opponentClass = '.js-accident-opponent-data';
		this.addOpponentButtonClass = '.js-clone-accidentopponent';

		this.sectionBgClasses = ['h-background', 'm-ves-rs-contentbox--grey'];
		this.altBgSectionSelectors = ['form > .m-basic-calculator-content-box', 'form > .m-ves-rs-contentbox'];

	}

	public toggleCommentTextArea(show: boolean): void {
		this.$ctx.find('.js-comment-field').toggleClass("h-hidden", !show);
	}

	public addCheckDataListener(changeListener: JQuery.EventHandler<HTMLElement>, submitListener: JQuery.EventHandler<HTMLElement>, backToStepListener: JQuery.EventHandler<HTMLElement>): void {
		const $form = this.getForm();

		$form.find('.js-back-step-1').on('click', backToStepListener);
		$form.find('.js-back-step-2').on('click', backToStepListener);
		$form.find('.js-back-step-3').on('click', backToStepListener);

		this.addListeners(changeListener, submitListener);
	}

	/**
 * adds listeners to form
 */
	protected addListeners(changeListener: JQuery.EventHandler<HTMLElement>, submitListener: JQuery.EventHandler<HTMLElement>): void {
		const $form = this.getForm();

		// init form listener
		$form.find('input, select, textarea').on('change', changeListener);
		$form.on('click', '.js-submit-form', submitListener);
		$form.on('submit', submitListener);

		// init all form elements from ew72
		S.Utils.FormAside.init();
		AppUtils.View.initFormElements();
		window.scrollTo(0, 0);
	}

	public addOpponentDeleteListener(deleteAccidentOpponentListener: JQuery.EventHandler<HTMLElement>): void {
		this.$ctx.find(`${this.opponentClass} .js-clone-remove`).last().on('click', deleteAccidentOpponentListener);
	}

	protected getCountryNameFromView(countrySelectId: string): string {
		let name: string;
		const $country = this.$ctx.find(`${countrySelectId} option:selected`);
		if ($country) {
			name = $country.text();
		}
		return name;
	}

	/**
	   * Updates the own description textarea headline and placeholder by the selected schadentyp
	   */
	public updateEigeneAngabenTextareaContainer(schadenTyp: string): void {
		if (schadenTyp !== undefined) {
			const $textArea = this.$ctx.find(this.ownDescriptionTextarea);
			const $headlines = this.$ctx.find(this.damageTypeHeadlineClass);
			const $headline = this.$ctx.find(`.schadentyp-${schadenTyp}-headline`);
			if ($headlines) {
				$headlines.toggleClass("h-hidden", true);
			}
			if ($textArea) {
				$textArea.attr("placeholder", this.getTextareaPlaceholder($textArea, schadenTyp));
			}
			if ($headline) {
				$headline.toggleClass("h-hidden", false);
			}
		}
	}

	protected getTextareaPlaceholder(textArea: JQuery, schadenTyp: string): string {
		const placeholder: string = textArea.data(`schadentyp-${schadenTyp}-placeholder`);
		if (placeholder && placeholder.length > 0) {
			return placeholder;
		}
		return "";
	}

	protected getEigeneAngabenBeschreibungFromView(): string {
		const $eigeneAngabenBeschreibung: JQuery = this.$ctx.find(this.ownDescriptionTextarea);
		if ($eigeneAngabenBeschreibung) {
			return $eigeneAngabenBeschreibung.val() as string;
		}
		return "";
	}

	public handleOffenderCloneCount(maxCount: number): void {
		if (this.$ctx.find(this.opponentClass).length === maxCount) {
			$(this.addOpponentButtonClass).toggleClass('h-hidden', true);
		} else {
			$(this.addOpponentButtonClass).toggleClass('h-hidden', false);
		}
	}

	protected getCostTypeDocumentsFromView(uploadContainerId: string): { [id: string]: VesDamageKostenbelegModel } {
		const $belegElem = this.$ctx.find(uploadContainerId).find(this.uploadStoredFileData);
		const belege: { [id: string]: VesDamageKostenbelegModel } = {};

		if ($belegElem.length > 0) {
			$belegElem.each((index, item) => {
				const beleg = new VesDamageKostenbelegModel();
				const $costCol = $(item).closest(this.uploadFileUploadClass).next(this.uploadFileSumClass);
				const $selected = $costCol.find('select option:selected');
				beleg.WaehrungBezeichnung = $selected.text();
				beleg.WaehrungKuerzel = $selected.val() as string;

				const betrag = parseFloat($costCol.find('input').val().toString().replace(/,/, "."));
				if (!isNaN(betrag)) {
					beleg.Betrag = Number(betrag.toFixed(2));
				}
				belege[$(item).attr(this.dataGuid)] = beleg;
			});
		}
		return belege;
	}


	protected getCostsSummary(): { [id: string]: string } {
		const summary: { [id: string]: string } = {};
		let $rightContent = this.$ctx.find(this.uploadCostContainerClass).find(this.uploadFileSumClass);
		if ($rightContent.length === 0) {
			$rightContent = this.$ctx.find(this.costTypeContainerClass).find(this.uploadFileSumClass);
		}

		$rightContent.each((index, item) => {
			const currency = $(item).find('select option:selected').val();
			const val = $(item).find('input').val().toString();
			const sum = parseFloat(val.replace(/,/, "."));

			if (!isNaN(sum)) {
				if (summary[currency.toString()]) {
					let totalCosts = parseFloat(summary[currency.toString()].replace(/,/, "."));
					totalCosts += sum;
					summary[currency.toString()] = totalCosts.toFixed(2).replace('.', ',');
				} else {
					summary[currency.toString()] = sum.toFixed(2).replace('.', ',');
				}
			}
		});
		return summary;
	}

	public updateSummaryCostsToView(kostenSummen: { [id: string]: string }): void {
		if (kostenSummen) {
			const $costSummary = this.$ctx.find(this.costTypeSummaryClass);
			const $source = $costSummary.find(`.${this.cloneSource}`).first();
			$costSummary.empty();
			$costSummary.append($source);
			if (Object.keys(kostenSummen).length > 0) {
				Object.keys(kostenSummen).forEach(key => {
					if (kostenSummen[key]) {
						const $clone = this.updateClone($source, key, kostenSummen[key]);
						$costSummary.append($clone);
					}
				});
				$costSummary.removeClass('is-hidden');
			}
		}
	}

	private updateClone(source: JQuery, currency: string, value: string): JQuery {
		const $clone = $(source).clone();
		const sumText = $clone.find(this.costSumLabelClass).first().text();
		$clone.attr('data-js-condition', `summarycost-${currency}`);
		$clone.find(this.costSumLabelClass).first().text(sumText + currency);
		$clone.find(this.costSumValueClass).first().text(value);
		$clone.find(this.costSumUnitClass).first().text(currency);
		$clone.removeClass('is-hidden');
		$clone.removeClass(this.cloneSource);

		return $clone;
	}

	public getUploadCostTypeDataFromView(costTypeElement: JQuery): JQuery {
		let uploadCostTypeId;
		switch ($(costTypeElement).val()) {
			case '1':
				uploadCostTypeId = this.uploadCostType1Id;
				break;
			case '2':
				uploadCostTypeId = this.uploadCostType2Id;
				break;
			case '3':
				uploadCostTypeId = this.uploadCostType3Id;
				break;
			case '4':
				uploadCostTypeId = this.uploadCostType4Id;
				break;
			case '5':
				uploadCostTypeId = this.uploadCostType5Id;
				break;
		}

		const $uploadCostTypeContainer = $(costTypeElement).parents(this.uploadCostTypeContentContainerClass).first().find(uploadCostTypeId);
		return $uploadCostTypeContainer.find(this.uploadCostTypeActiveUploadsClasses);
	}

	/**
	 * Iterates through DOM on summary page
	 * returns model for all data on summary page
	 */
	public getZusammenfassungStrukturApi(): ZusammenfassungStrukturApi {
		const apiModel = new ZusammenfassungStrukturApi();

		apiModel.Title = this.$ctx.find(this.checkPageHeadlineClass).text();

		const paragraphs: ParagraphApi[] = [];

		this.$ctx.find(this.checkPageParagraphClass).each((key, element) => {
			const paragraph = new ParagraphApi();

			paragraph.Title = $(element).find('h2').text();

			const rows: RowApi[] = [];
			$(element).find(this.checkPageRowClass).each((key, value) => {
				rows.push(this.getZusammenfassungRowValue(value));
			});
			paragraph.Rows = rows;

			paragraphs.push(paragraph);
		});
		apiModel.Paragraphs = paragraphs;

		const footer = new FooterAbsatzApi();

		footer.Title = this.$ctx.find(this.checkPageFooterTitleClass).text();

		footer.Text = '';
		this.$ctx.find(this.checkPageFooterTextClass).each((key, element) => {
			footer.Text += `${this.removeAnchorsFromText(element.innerText)}\r\n`;
		});

		const lightboxcontent = $(this.lightboxClass).clone().get();
		footer.ZusatzText = this.retrieveLightboxContent(lightboxcontent, true);

		apiModel.Footer = footer;

		// 83773 Dokumente haben teilweise keine Labels
		if (this._emptyLabels > 0) {
			T.Utils.Logger.log(`VesDamageViewService.getZusammenfassungStrukturApi: ${this._emptyLabels} Labels wurden leer übertragen`);
		}

		return apiModel;
	}

	protected getZusammenfassungRowValue(object: HTMLElement): RowApi {
		const row = new RowApi();

		row.Name = $(object).find(this.checkPageRowHeadlineClass).text();
		const singlevalue = $(object).find(this.checkPageRowValueClass).text();

		if (singlevalue) {
			row.Value = singlevalue;
		} else {
			//if no value, check for multiple values
			const multiple = $(object).find(this.checkPageRowMultipleClass);
			if (multiple && multiple.length > 0) {
				const rowValues: string[] = [];
				$(multiple).find(this.checkPageRowMultipleValueClass).each((key, value) => {
					rowValues.push(value.innerText);
				});
				row.Values = rowValues;
			}
		}

		if (T.Utils.Helper.isEmptyString(row.Name)) {
			this._emptyLabels++;
		}

		return row;
	}

	protected removeAnchorsFromText(content: string): string {
		content = content.replace(/<\/?a(.*?)>/g, '');
		return content;
	}


	public setTimestampToView(timestamp: string): void {
		const date = moment(timestamp).format('DD.MM.YYYY');
		const time = moment(timestamp).format('HH:mm');
		const timeString = `${date} um ${time}`;
		const successText = this.$ctx.find(this.successPageSuccessTextClass);
		if (successText.length > 0) {
			const htmlText = $(successText).html().replace('{Timestamp}', timeString);
			$(successText).html(htmlText);
		}
	}

	protected retrieveLightboxContent(lightboxcontent: HTMLElement[], innerTextOnly: boolean): string {
		const idArr: Array<string> = [];
		this.$ctx.find('a.js-lightbox').each((index, item) => {
			idArr.push($(item).attr('href'));
		});

		let output = "";
		lightboxcontent.forEach(element => {
			if ($.inArray(`#${element.getAttribute('id')}`, idArr) !== -1) {
				if (innerTextOnly) {
					const content = $(element).find(".m-basic-text");
					if (content && content.length > 0) {
						output += content.html();
					}
				} else {
					element.removeAttribute("class");
					output += element.outerHTML;
				}
			}
		});
		return output;
	}

	public getInputFileIds(uploadContainer: JQuery): Array<string> {
		const inputIds: Array<string> = [];

		$(uploadContainer).find('input[type="file"]').each((index, element) => {
			inputIds.push($(element).attr('id'));
		});

		return inputIds;
	}

	public toggleAndFillKontoinhaber(user?: User): void {
		const $abweichenderKontoInhaberCheckbox = this.$ctx.find(this.abweichenderKontoInhaberName);
		const $abweichenderKontoInhaberRow = this.$ctx.find(".js-abweicherkontoinhaber-row");
		const $kontoinhaber = this.$ctx.find(this.kontoinhaberName);

		if ($abweichenderKontoInhaberCheckbox.is(':checked')) {
			const $kontoinhaberValue = $kontoinhaber.val() as string;

			if ($kontoinhaberValue.length > 0 && $kontoinhaberValue === this.getPolicyOwnerVornameAndName(user)) {
				$kontoinhaber.val("");
			}

			$abweichenderKontoInhaberRow.toggleClass("h-hidden", false);
		}
		else {
			$abweichenderKontoInhaberRow.toggleClass("h-hidden", true);
			$kontoinhaber.val(this.getPolicyOwnerVornameAndName(user));
		}
	}

	protected getPolicyOwnerVornameAndName(user?: User): string {
		if (user) {
			return `${user.UserData.Vorname} ${user.UserData.Nachname}`;
		} else {
			const $vorname = this.$ctx.find(this.vornameName).val() as string;
			const $nachname = this.$ctx.find(this.nachnameName).val() as string;
			if ($vorname.length > 0 && $nachname.length > 0) {
				return `${$vorname} ${$nachname}`;
			}
		}
		return "";
	}

	protected getPersonalDataModelFromPersonalDataView(): PersonalDataModel {
		const model = new PersonalDataModel();
		const formData = this.getFormData();

		model.Mitgliedsnummer = formData.Mitgliedsnummer;
		model.Vorname = formData.Vorname;
		model.Nachname = formData.Nachname;
		model.Adresse.Ort = formData.Ort;
		model.Adresse.Postleitzahl = formData.Plz;
		model.Adresse.StrasseHausnummer = formData.StrasseHausnummer;
		model.TelefonNummer = formData.TelefonNummer;
		model.TelefonVorwahl = formData.TelefonVorwahl;
		model.Email = formData.Email;

		return model;
	}

	public getFileIdsFromView(): string[] {
		const fileIds: string[] = [];

		this.$ctx.find(`[${this.dataGuid}]`).each((index, element) => {
			const guid = $(element).attr(this.dataGuid);
			fileIds.push(guid);
		});

		return fileIds;
	}

	public getInputFieldOnFileGuid(fileGuid: string): JQuery {
		const fileDiv = this.$ctx.find(`[${this.dataGuid}="${fileGuid}"]`);
		if (fileDiv.length > 0) {
			return $(fileDiv).parents('.js-upload').find('input[type="file"]');
		}

		return undefined;
	}

	public restoreSectionBackground(): JQuery {
		return super.restoreSectionBackground(this.altBgSectionSelectors, ($section) => {
			const hasBg = this.sectionBgClasses.some(value => $section.hasClass(value));
			$section.find('.a-basic-input-textarea').toggleClass('a-basic-input-textarea--white', hasBg);
			$section.find('.a-basic-input-text').toggleClass('a-basic-input-text--white', hasBg);
			if (hasBg && $section.hasClass('m-ves-rs-contentbox')) {
				$section.find('.m-ves-rs-contentbox').toggleClass('m-ves-rs-contentbox--grey', true);
			}
		});
	}

	public showLightBoxForForeignLanguages(): void {
		const $lightbox = this.$ctx.find('.js-foreign-lang-lightbox');
		const lang = navigator.language;
		const allowedLanguages = ["de", "de-AT", "de-BE", "de-CH", "de-DE", "de-IT", "de-LI", "de-LU"];

		if ($lightbox.length && !allowedLanguages.includes(lang)) {
			AppUtils.View.displayDialog($lightbox);
		}
	}

	public checkCountryCodeIban(): void {
		const control = this.$ctx.find(`[name="Iban"]`);
		if (control) {
			const ibanValue = control.val().toString();
			if (ibanValue && ibanValue.length >= 2) {
				const countryCode = ibanValue.substring(0, 2).toUpperCase();
				const includesCountryCode = this.options.app.countrycodeswithsepasupport.includes(countryCode);
				this.handleBicVisibility(includesCountryCode);
			} else {
				this.handleBicVisibility(true);
			}
		}
	}
	private handleBicVisibility(hidden: boolean): void {
		this.$ctx.find('.js-bankaccount-bic').toggleClass('h-hidden', hidden);
		if (hidden) {
			this.$ctx.find(`[name="Bic"]`).val('');
		}
	}
}
